import { validate as validateEmail } from "email-validator";
import { atom, selector, selectorFamily } from "recoil";
import { User, UserRequest, UserRequestTouched } from "../types/users";
import { PaginatedResponse } from "../types/app";
import { USER_DEFAULTS, USER_TOUCHED_DEFAULTS } from "../constants/users";
import { validatePhone } from "../utils/forms";
import { validatePassword } from "../utils/password";

export const usersState = atom<PaginatedResponse<User> | undefined>({
  key: "app.users",
  default: undefined,
});

export const usersIdsState = selector<string[] | undefined>({
  key: "app.users.ids",
  get: ({ get }) => {
    const users = get(usersState);
    if (!users) return;
    return users.items.map((user) => user._id);
  },
});

export const userState = selectorFamily<User | undefined, string>({
  key: "app.user",
  get:
    (id) =>
    ({ get }) => {
      const users = get(usersState);
      return users?.items.find((user) => user._id === id);
    },
});

export const searchUsersState = atom<string | undefined>({
  key: "app.users.search",
  default: undefined,
});

export const usersPageState = atom({
  key: "app.users.page",
  default: 1,
});

export const updateUsersState = atom<string | undefined>({
  key: "app.users.update",
  default: undefined,
});

export const loadingUsersState = atom({
  key: "app.users.loading",
  default: false,
});

export const editUserState = atom<UserRequest>({
  key: "app.users.edit",
  default: USER_DEFAULTS,
});

export const editUserTouchedState = atom<UserRequestTouched>({
  key: "app.users.edit.touched",
  default: USER_TOUCHED_DEFAULTS,
});

export const editUserValidState = selector<{
  [key in keyof UserRequest]: boolean;
}>({
  key: "app.users.edit.valid",
  get: ({ get }) => {
    const fields = get(editUserState);

    // SET ALL FIELDS TO BE VALID
    const validFields = Object.fromEntries(
      Object.keys(fields).map((key) => [key, true])
    ) as {
      [key in keyof UserRequest]: boolean;
    };

    // VALIDATE REQUIRED FIELDS
    validFields.email = validateEmail(fields.email);
    if (!fields.firstName) {
      validFields.firstName = false;
    }
    if (!fields.lastName) {
      validFields.lastName = false;
    }
    if (fields.phone) {
      validFields.phone = validatePhone(fields.phone);
    }
    if (fields.userType === undefined || fields.userType !== 0) {
      validFields.userType = false;
    }
    validFields.password =
      Object.keys(validatePassword(fields.password) ?? {}).length === 0;

    return validFields;
  },
});

export const editConfirmPasswordUserState = atom({
  key: "app.users.edit.confirmPassword",
  default: "",
});

export const UserChangePasswordState = atom({
  key: "app.users.edit.changePassword",
  default: false,
});

export const editUserErrorState = selector({
  key: "app.users.edit.error",
  get: ({ get }) => {
    const validFields = get(editUserValidState);
    return Object.values(validFields).some((valid) => !valid);
  },
});
